import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import ExtraLessonEditModal from './ExtraLessonEditModal';
import SingleFilterChip from '@/components/SingleFilterChip';

/**
 * Карточки доп. занятий: предмет + тип + учитель + цены. Это «справочник
 * связок», которые потом ставятся в расписание (этап 4).
 *
 * Фильтры в URL: archive, type, subject_id, teacher_id, edit.
 */
const URL_SCHEMA = {
  archive: '0',     // '0' — только активные, '1' — включая архив
  type: '',         // '', 'individual', 'group'
  subject_id: '',
  teacher_id: '',
  edit: '',
};

const TYPE_LABELS = {
  individual: 'Индивидуально',
  group:      'Группа',
};

function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' ₽';
}

export default function ExtraLessonsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('extra.lessons', 'write');
  const canDelete = can('extra.lessons', 'delete');

  const [params, setParams] = useUrlState(URL_SCHEMA);

  // Справочники — для фильтров и для модалки. Грузим один раз.
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [rows, setRows]    = useState([]);
  const [loading, setLoad] = useState(true);
  const [error, setError]  = useState(null);

  // Загрузка справочников (предметы + учителя)
  useEffect(() => {
    api.get('/api/extra-subjects')
      .then((res) => setSubjects(res.rows || []))
      .catch(() => {});
    api.get('/api/extra-lessons/teachers')
      .then((res) => setTeachers(res.rows || []))
      .catch(() => {});
  }, []);

  const reload = () => {
    setLoad(true); setError(null);
    const qs = new URLSearchParams();
    qs.set('archive', params.archive || '0');
    if (params.type)       qs.set('type', params.type);
    if (params.subject_id) qs.set('subject_id', params.subject_id);
    if (params.teacher_id) qs.set('teacher_id', params.teacher_id);
    api.get('/api/extra-lessons?' + qs.toString())
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  };
  useEffect(reload, [params.archive, params.type, params.subject_id, params.teacher_id]);

  const onDelete = async (l) => {
    if (!canDelete) return;
    const subjName = l.subject?.name || '?';
    if (!confirm(`Удалить занятие «${subjName}» (${TYPE_LABELS[l.type]})?`)) return;
    try {
      await api.delete('/api/extra-lessons/' + l.id);
      setRows((prev) => prev.filter((r) => r.id !== l.id));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  const editing = useMemo(() => {
    if (!params.edit) return null;
    if (params.edit === 'new') return 'new';
    return rows.find((r) => r.id === parseInt(params.edit, 10)) || null;
  }, [params.edit, rows]);

  const hasFilters = params.type || params.subject_id || params.teacher_id;

  return (
    <>
      <Topbar
        breadcrumb={['Доп. занятия', 'Занятия']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="filterbar">
        <div className="filterbar__row">
          <SingleFilterChip
            label="Предмет"
            value={params.subject_id || ''}
            options={subjects.filter((s) => !s.archive).map((s) => ({ v: String(s.id), label: s.name }))}
            onChange={(v) => setParams({ subject_id: v })}
            placeholder="любой"
            nullLabel="Любой предмет"
          />
          <SingleFilterChip
            label="Учитель"
            value={params.teacher_id || ''}
            options={teachers.map((t) => ({ v: String(t.id), label: t.name }))}
            onChange={(v) => setParams({ teacher_id: v })}
            placeholder="любой"
            nullLabel="Любой учитель"
          />
          <SingleFilterChip
            label="Тип"
            value={params.type || ''}
            options={[
              { v: 'individual', label: 'Индивидуально' },
              { v: 'group',      label: 'Группа' },
            ]}
            onChange={(v) => setParams({ type: v })}
            placeholder="любой"
            nullLabel="Любой тип"
          />
          <SingleFilterChip
            label="Видимость"
            value={params.archive === '1' ? '1' : ''}
            options={[
              { v: '1', label: 'Все, включая архив' },
            ]}
            onChange={(v) => setParams({ archive: v || '0' })}
            placeholder="только активные"
            nullLabel="Только активные"
          />

          {hasFilters && (
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => setParams({ type: '', subject_id: '', teacher_id: '' })}
            >
              Сбросить
            </button>
          )}

          <div className="filterbar__spacer" />
          <span className="filterbar__meta">Найдено: <b>{rows.length}</b></span>

          <div className="filterbar__rightActions">
            <button
              className="btn btn--primary btn--sm"
              onClick={() => setParams({ edit: 'new' })}
              disabled={!canWrite}
              title={canWrite ? undefined : 'Нет прав на запись'}
            >
              <Icon name="plus" size={13} /> Добавить занятие
            </button>
          </div>
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Предмет</th>
                <th style={{ width: 130 }}>Тип</th>
                <th>Учитель</th>
                <th style={{ width: 130, textAlign: 'right' }}>С ученика</th>
                <th style={{ width: 130, textAlign: 'right' }}>Учителю</th>
                <th style={{ width: 110 }}>Статус</th>
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)' }}>Загрузка…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)' }}>Занятий не найдено.</td></tr>
              )}
              {rows.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => canWrite && setParams({ edit: String(l.id) })}
                  style={{ cursor: canWrite ? 'pointer' : 'default' }}
                >
                  <td>
                    <div style={{ fontWeight: 500 }}>{l.subject?.name || '—'}</div>
                    {l.subject?.onec
                      ? <div className="muted" style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{l.subject.onec.code}</div>
                      : <div className="muted" style={{ fontSize: 11, color: 'var(--red-fg)' }}>не привязан к 1С</div>}
                  </td>
                  <td>
                    <span className={'pay-st ' + (l.type === 'group' ? 'pay-st--pending' : 'pay-st--ok')}>
                      {TYPE_LABELS[l.type] || l.type}
                    </span>
                  </td>
                  <td>
                    <div>{l.teacher?.name || '—'}</div>
                    {l.teacher?.role === 'administrator' && (
                      <div className="muted" style={{ fontSize: 11 }}>администратор · совместитель</div>
                    )}
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>{fmtMoney(l.priceStudent)}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{fmtMoney(l.priceTeacher)}</td>
                  <td>
                    {l.archive
                      ? <span className="pay-st pay-st--mute">в архиве</span>
                      : <span className="pay-st pay-st--ok">активно</span>}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="ref-row-actions">
                      {canWrite && (
                        <button onClick={() => setParams({ edit: String(l.id) })} title="Редактировать">
                          <Icon name="edit" size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button className="is-danger" onClick={() => onDelete(l)} title="Удалить">
                          <Icon name="x" size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <ExtraLessonEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          lesson={editing === 'new' ? null : editing}
          subjects={subjects}
          teachers={teachers}
          onClose={() => setParams({ edit: '' })}
          onSaved={(l) => {
            setRows((prev) => {
              const i = prev.findIndex((r) => r.id === l.id);
              if (i >= 0) { const n = prev.slice(); n[i] = l; return n; }
              return [...prev, l];
            });
          }}
        />
      )}
    </>
  );
}
